import axios, { CancelToken, AxiosResponse, AxiosError } from 'axios'

import { authApi } from 'interface/endpoints'
import { ApiAccess } from 'features/session/types'

export * from './apiFetch'
export * from './apiFetchList'
export * from './apiPost'
export * from './apiPostList'

type ErrorInfo = {
  errorCode: number
  message: string
  extend?: string
}
class ApiError extends Error {
  errorCode: number
  constructor(error: ErrorInfo) {
    super(error.message + (error.extend ? ` [${error.extend}]` : ''))
    this.errorCode = error.errorCode
  }
}

export interface FileResponse {
  blob: Blob
  fileName: string
}
const parseFileResponse = (
  response: AxiosResponse,
  resolve: (response: AxiosResponse<FileResponse> | PromiseLike<AxiosResponse<FileResponse>>) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reject: (reason?: any) => void
) => {
  if (response.data?.type.startsWith('image/')) {
    const blob = new Blob([response.data], { type: response.data.type })
    const cd = response.headers['content-disposition']
    response.data = {
      blob: blob,
      fileName: cd ? cd.substring(cd.indexOf("''") + 2, cd.length) : '',
    }
    resolve(response)
  } else if (response.data?.type === 'application/json' && response.data instanceof Blob) {
    response.data.text()
      .then(text => {
        response.data = JSON.parse(text)
        resolve(response)
      })
      .catch(reject)
  } else {
    reject(response.data)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNotFound = (reason: any) => {
  return (reason?.errorCode === 104 || reason?.errorCode === 105) ? true : false
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNotFoundFile = (reason: any) => {
  return (reason?.errorCode === 104 || reason?.errorCode === 105) ? true : false
}

const callApiNoToken = async <ResponseType>(
  url: string,
  callBack: () => Promise<AxiosResponse<ResponseType> | Error>
): Promise<ResponseType> => {
  const response = await callBack().catch(err => err)
  if (response instanceof AxiosError) {
    const err = response as AxiosError
    if (err.response?.status !== 401) {
      console.error(url, err)
    }
    throw err
  }
  if (response.data.errorCode) {
    throw new ApiError(response.data as ErrorInfo)
  }
  return response.data as ResponseType
}

const callApiWithToken = async <ResponseType>(
  url: string,
  callBack: (accessToken: string) => Promise<AxiosResponse<ResponseType> | Error>,
  access: ApiAccess,
  isRetried = false
): Promise<ResponseType> => {
  const accessToken = access.accessToken
  if (accessToken === null) {
    const newAccessToken = await ApiGetWithLoginToken<string>(access.loginToken, authApi('/token'))
      .catch(reason => access.onUnAuthorize(reason))
    if (!newAccessToken) throw Error('invalide access token')
    access.accessToken = newAccessToken
    access.onGetAccessToken(newAccessToken)
    return callApiWithToken(url, callBack, access, true)
  }
  const response = await callBack(accessToken).catch(err => err)
  if (response instanceof AxiosError) {
    const err = response as AxiosError
    if (err.response?.status === 401) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorData: any = err.response?.data
      if (!isRetried && errorData?.code === 2) {
        access.accessToken = null
        access.onExpiredAccessToken()
        return await callApiWithToken(url, callBack, access)
      }
      access.onUnAuthorize(err)
    } else {
      console.error(url, err)
    }
    throw err
  }
  if (response?.data?.errorCode) {
    throw new ApiError(response.data as ErrorInfo)
  }
  return response.data as ResponseType
}

export const ApiGetNoToken = async <ResponseType>(
  url: string,
  cancelToken?: CancelToken
) => callApiNoToken<ResponseType>(url, () => axios.get(url, { cancelToken }))

export const ApiPostNoToken = async <ResponseType>(
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  cancelToken?: CancelToken
) => callApiNoToken<ResponseType>(url, () => axios.post(url, data, { cancelToken }))

export const ApiGetFileNoToken = (
  url: string
): Promise<FileResponse> => callApiNoToken<FileResponse>(
  url,
  () => new Promise<AxiosResponse<FileResponse>>(
    (resolve, reject) => {
      axios
        .get(url, { responseType: 'blob' })
        .then(response => {
          const blob = new Blob([response.data], { type: response.data.type })
          const cd = response.headers['content-disposition']
          response.data = {
            blob: blob,
            fileName: cd ? cd.substring(cd.indexOf("''") + 2, cd.length) : '',
          }
          resolve(response)
        })
        .catch(reason => reject(reason))
    }
  )
)

export const ApiGetWithLoginToken = async <ResponseType>(
  loginToken: string,
  url: string,
  cancelToken?: CancelToken
) => callApiNoToken<ResponseType>(url, () => axios.get(url, {
  headers: { authorization: loginToken },
  cancelToken
}))

export const ApiPostWithLoginToken = async <ResponseType>(
  loginToken: string,
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  cancelToken?: CancelToken
) => callApiNoToken<ResponseType>(url, () => axios.post(url, data, {
  headers: {
    authorization: loginToken
  },
  cancelToken
}))

export const ApiGetWithToken = <ResponseType>(
  access: ApiAccess,
  url: string,
  cancelToken?: CancelToken
) => callApiWithToken<ResponseType>(url, (accessToken) => axios.get(url, {
  headers: {
    authorization: accessToken
  },
  cancelToken
}), access)

export const ApiGetStreamWithToken = async <ResponseType>(
  access: ApiAccess,
  url: string | undefined,
  initialize: (counts: number) => void,
  callback: (data: ResponseType) => Promise<void>,
  signal: AbortSignal | null
): Promise<void> => {
  if (!url) return
  const accessToken = access.accessToken
  if (accessToken === null) {
    const newAccessToken = await ApiGetWithLoginToken<string>(access.loginToken, authApi('/token'))
      .catch(reason => access.onUnAuthorize(reason))
    if (!newAccessToken) throw Error('invalide access token')
    access.accessToken = newAccessToken
    access.onGetAccessToken(newAccessToken)
    return ApiGetStreamWithToken(access, url, initialize, callback, signal)
  }
  const response = await fetch(url, {
    headers: { authorization: accessToken },
    signal: signal ?? undefined
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  if (!response.body) {
    throw new Error('HTTP error! response body is empty')
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let lineBuffer = ''
  let initialized = false
  let closed = false
  do {
    const { done, value } = await reader.read()
    if (done) {
      if (lineBuffer.trim() !== '') {
        try {
          if (initialized === false) {
            const parsedItem = JSON.parse(lineBuffer) as { count: number }
            initialize(parsedItem.count)
            initialized = true
          } else {
            const parsedItem = JSON.parse(lineBuffer) as ResponseType
            callback(parsedItem)
          }
        } catch (parseError) {
          console.warn('Failed to parse final JSONL line:', lineBuffer, parseError);
        }
      }
      console.log(`Stream finished.: ${url}`)
      closed = true
    } else {
      lineBuffer += decoder.decode(value, { stream: true })
      const lines = lineBuffer.split('\n')
      lineBuffer = lines.pop() ?? ''

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine === '') continue; // 空行はスキップ
        try {
          if (initialized === false) {
            const parsedItem = JSON.parse(trimmedLine) as { count: number }
            initialize(parsedItem.count)
            initialized = true
          } else {
            const parsedItem = JSON.parse(trimmedLine) as ResponseType
            await callback(parsedItem)
          }
        } catch (parseError) {
          console.warn('Failed to parse JSONL line:', trimmedLine, parseError);
          // パースエラーが発生した場合でも、他の有効な行の処理は続行
        }
      }
    }
  } while (closed === false)
}

export const ApiPostWithToken = <ResponseType>(
  access: ApiAccess,
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  cancelToken?: CancelToken,
  webSocketId?: string
) => callApiWithToken<ResponseType>(url, (accessToken: string) => axios.post(url, data, {
  headers: {
    authorization: accessToken,
    'websocket-id': webSocketId
  },
  cancelToken
}), access)

export const ApiPatchWithToken = <ResponseType>(
  access: ApiAccess,
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  cancelToken?: CancelToken,
  webSocketId?: string
) => callApiWithToken<ResponseType>(url, (accessToken: string) => axios.patch(url, data, {
  headers: {
    authorization: accessToken,
    'websocket-id': webSocketId
  },
  cancelToken
}), access)

export const ApiDeleteWithToken = <ResultType>(
  access: ApiAccess,
  url: string,
  cancelToken?: CancelToken,
  webSocketId?: string
) => callApiWithToken<ResultType>(url, (accessToken: string) => axios.delete(url, {
  headers: {
    authorization: accessToken,
    'websocket-id': webSocketId
  },
  cancelToken
}), access)

export const ApiGetFileWithToken = (
  access: ApiAccess,
  url: string,
  cancelToken?: CancelToken
): Promise<FileResponse | null> => callApiWithToken<FileResponse>(url, (accessToken) => new Promise<AxiosResponse<FileResponse>>(
  (resolve, reject) => 
    axios
      .get(url, {
        responseType: 'blob',
        headers: {
          authorization: accessToken
        },
        cancelToken
      })
      .then(response => parseFileResponse(response, resolve, reject))
      .catch(reject)
), access)

export const ApiPostFileWithToken = (
  access: ApiAccess,
  url: string,
  data: FormData,
  cancelToken?: CancelToken,
  webSocketId?: string
): Promise<FileResponse> => callApiWithToken<FileResponse>(url, (accessToken) => new Promise<AxiosResponse<FileResponse>>(
  (resolve, reject) =>
    axios
      .post(url, data, {
        headers: {
          responseType: 'blob',
          'Content-Type': 'multipart/form-data',
          authorization: accessToken,
          'websocket-id': webSocketId
        },
        cancelToken
      })
      .then(response => parseFileResponse(response, resolve, reject))
      .catch(reject)
), access)
